/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createBrowserHistory,
  type History,
  type Path,
  type LocationState,
  type LocationDescriptorObject,
  type Href,
} from 'history'
import qs from 'query-string'
import {
  appendPartnerScopeToPath,
  isExternalPath,
  PARTNER_SCOPE_QUERY_KEYS,
  readPartnerScopeParams,
} from '../../utils/query-params/partnerScopeQueryParams.utils'

export interface PatchedLocationDescriptorObject<
  TQuery,
  S = LocationState,
> extends LocationDescriptorObject<S> {
  query?: TQuery
}

export interface PatchedHistory<
  TQuery = any,
  HistoryLocationState = LocationState,
> extends History<HistoryLocationState> {
  push(path: Path, state?: HistoryLocationState): void
  push(location: PatchedLocationDescriptorObject<TQuery, HistoryLocationState>): void
  replace(path: Path, state?: HistoryLocationState): void
  replace(location: PatchedLocationDescriptorObject<TQuery, HistoryLocationState>): void
  createHref(location: PatchedLocationDescriptorObject<TQuery, HistoryLocationState>): Href
}

/**
 * Keys of locations THIS history's own push/replace minted — the "was this entry reached
 * by an in-app navigation" signal `useHasInAppBackEntry` guards on. Needed because the
 * TanStack shell plants a `key` in `history.state` at router init (even on cold starts),
 * so raw `location.key` truthiness no longer distinguishes app navigations.
 */
const appWrittenKeys = new Set<string>()

export const isAppWrittenLocationKey = (key: string | undefined): boolean =>
  !!key && appWrittenKeys.has(key)

function createHistory(history: History = createBrowserHistory()): PatchedHistory {
  // using these to compare methods later for TS to infer signature
  const { push, replace } = history

  function patch(method: 'push' | 'replace' | 'createHref') {
    const originalMethod = history[method]

    // had to cast here to get around an issue where TS was
    // interpreting history[method] as an intersection of the three methods
    // instead of a union when using it on the left hand side of a variable assignment
    // eslint-disable-next-line no-param-reassign
    ;(history[method] as typeof originalMethod) = patchedMethod

    function patchedMethod(
      location: Path | PatchedLocationDescriptorObject<any, LocationState>,
      state?: LocationState
    ) {
      if (typeof location === 'string' && (originalMethod === push || originalMethod === replace)) {
        const mergedLocation = appendPartnerScopeToPath(location, history.location.search)

        // Skip no-op replace calls to avoid unnecessary re-render cascades
        if (
          originalMethod === replace &&
          mergedLocation === history.location.pathname + history.location.search
        ) {
          return
        }

        const stringResult = (originalMethod as typeof push)(mergedLocation, state)
        if (history.location.key) appWrittenKeys.add(history.location.key)
        return stringResult
      }

      const declared = location as PatchedLocationDescriptorObject<any, LocationState>
      const callerDeclaresQuery = declared.query !== undefined
      const declaredKeys = new Set<string>()

      // eslint-disable-next-line prefer-const
      let { query = {}, pathname, search, ...others } = declared
      pathname = pathname || history.location.pathname

      Object.keys(query).forEach(key => {
        declaredKeys.add(key)
        if (query[key] === 'null' || query[key] === 'undefined' || query[key] == null) {
          delete query[key]
        }
      })

      const parsedSearch = search ? qs.parse(search) : {}
      Object.keys(parsedSearch).forEach(key => declaredKeys.add(key))

      if (window.location.search.includes('debugAnalytics=true')) {
        query = {
          ...query,
          debugAnalytics: true,
        }
      }

      if (search) {
        query = {
          ...parsedSearch,
          ...query,
        }
      }

      if (!callerDeclaresQuery && !isExternalPath(pathname)) {
        const scope = readPartnerScopeParams(history.location.search)
        PARTNER_SCOPE_QUERY_KEYS.forEach(key => {
          if (declaredKeys.has(key)) return
          const values = scope[key]
          if (values) query[key] = values
        })
      }

      const newSearch = qs.stringify(query)

      // Skip no-op replace calls to avoid unnecessary re-render cascades
      if (
        originalMethod === replace &&
        pathname === history.location.pathname &&
        newSearch === qs.stringify(qs.parse(history.location.search))
      ) {
        return
      }

      const payload = {
        ...others,
        pathname,
        search: newSearch,
      }

      const result = originalMethod(payload)
      if (history.location.key) appWrittenKeys.add(history.location.key)
      return result
    }
  }

  patch('push')
  patch('replace')
  patch('createHref')

  return history
}

export default createHistory
